import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '../stores/auth-store.js';

export function usePushNotifications() {
  const [permission, setPermission] = useState<'granted' | 'denied' | 'default'>('default');
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const mod = await import('@capacitor/push-notifications' as string);
        const PushNotifications = mod.PushNotifications;
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          setPermission('granted');
          await PushNotifications.register();

          PushNotifications.addListener('registration', async (regToken: { value: string }) => {
            try {
              await fetch(`${import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : ''}/v1/notifications/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ token: regToken.value, platform: Capacitor.getPlatform() }),
              });
            } catch (e) { console.error('Push registration failed:', e); }
          });

          PushNotifications.addListener('pushNotificationReceived', (notification: { title?: string; body?: string }) => {
            console.log('Push received:', notification);
          });

          PushNotifications.addListener('pushNotificationActionPerformed', (action: { notification: { data?: { url?: string } } }) => {
            const url = action.notification.data?.url;
            if (url) window.location.href = url;
          });
        } else {
          setPermission('denied');
        }
      } catch (e) {
        console.error('Push notification setup failed:', e);
      }
    } else if ('Notification' in window) {
      // Web push
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted' && 'serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: undefined, // VAPID key would go here
          });
          await fetch(`${import.meta.env.PROD ? 'https://cardealeros-api.bowling-anthony.workers.dev' : ''}/v1/notifications/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ subscription: sub.toJSON(), platform: 'web' }),
          });
        } catch (e) {
          console.error('Web push subscription failed:', e);
        }
      }
    }
  };

  return { permission, requestPermission };
}
